import type {
  AccountMailOutcome,
  ApiSuccess,
  CreateUserRequest,
  CreateUserResponse,
  EmailStatusResponse,
  ListUsersQuery,
  ResetPasswordRequest,
  ResetPasswordResponse,
  UpdateUserRequest,
  UserSummary,
} from '@todo/shared';
import { api, del, get, getPaged, patch, post } from './client';

/**
 * Typed wrappers around /api/users. Components import these, never `api`
 * directly, so a URL change happens in exactly one place.
 */
export const userApi = {
  /** GET /api/users - paginated, so this returns the envelope with `meta`. */
  list(query: ListUsersQuery = {}): Promise<ApiSuccess<UserSummary[]>> {
    return getPaged<UserSummary[]>('/users', query as Record<string, unknown>);
  },

  /** GET /api/users/:id */
  byId(id: string): Promise<UserSummary> {
    return get<UserSummary>(`/users/${id}`);
  },

  /**
   * POST /api/users - the account starts INVITED until its first sign-in.
   *
   * Returns the person PLUS `welcomeMail`: 'sent' | 'skipped' | 'failed'.
   * The caller is expected to tell the administrator when it is not
   * 'sent', because there is no self-service password reset to fall back
   * on - an unsent welcome mail means a person who cannot get in.
   */
  create(body: CreateUserRequest): Promise<CreateUserResponse> {
    return post<CreateUserResponse>('/users', body);
  },

  /** GET /api/users/email-status - what creating this email would do:
   *  'free' | 'in-this-org' | 'exists-elsewhere'. Drives the form's link mode. */
  emailStatus(email: string): Promise<EmailStatusResponse> {
    return get<EmailStatusResponse>(
      `/users/email-status?email=${encodeURIComponent(email)}`,
    );
  },

  /** PATCH /api/users/:id - only the fields present change. */
  update(id: string, body: UpdateUserRequest): Promise<UserSummary> {
    return patch<UserSummary>(`/users/${id}`, body);
  },

  /** DELETE /api/users/:id - soft delete; 204 on success. */
  remove(id: string): Promise<void> {
    return del(`/users/${id}`);
  },

  /**
   * POST /api/users/:id/password - admin sets a new temporary password.
   *
   * Was 204; now answers with `resetMail` for the same reason as create.
   */
  async resetPassword(
    id: string,
    body: ResetPasswordRequest,
  ): Promise<AccountMailOutcome> {
    const { data } = await api.post<ApiSuccess<ResetPasswordResponse>>(
      `/users/${id}/password`, body,
    );
    return data.data.resetMail;
  },

  /**
   * POST /api/users/:id/avatar
   *
   * The File goes straight into the body - no FormData. The server reads it
   * with express.raw(), so there is no multipart to parse on either side.
   * The Content-Type must be the real image type; the server re-checks the
   * magic bytes anyway and rejects anything that disagrees.
   */
  async setAvatar(id: string, file: File): Promise<void> {
    await api.post(`/users/${id}/avatar`, file, {
      headers: { 'Content-Type': file.type },
    });
  },

  /** The <img> src. Returns 404 when the person has no photo. */
  avatarUrl(id: string): string {
    return `/api/users/${id}/avatar`;
  },
};
